import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import unifiedApi from '../services/unifiedApi';

const GROUPS = [
  {
    id: 'parents',
    label: 'Notifications parents',
    fields: [
      {
        key: 'approaching_m',
        label: 'Seuil approche (m)',
        unit: 'm',
        min: 100,
        max: 5000,
        help: 'Distance à laquelle le parent est notifié que le bus approche. Bas = alerte plus tardive mais plus précise.',
      },
      {
        key: 'approaching_eta_min',
        label: 'ETA approche max (min)',
        unit: 'min',
        min: 1,
        max: 30,
        help: "Délai maximum estimé avant l'arrêt pour déclencher la notification d'approche.",
      },
      {
        key: 'arrival_m',
        label: "Seuil arrivée vol d'oiseau (m)",
        unit: 'm',
        min: 10,
        max: 500,
        help: "Distance à vol d'oiseau considérée comme « arrivé à l'arrêt ». Bas = plus précis mais risque de ne jamais se déclencher si le GPS dérive légèrement.",
      },
      {
        key: 'arrival_road_m',
        label: 'Seuil arrivée route (m)',
        unit: 'm',
        min: 10,
        max: 500,
        help: "Distance par la route (Mapbox) considérée comme « arrivé ». Sert de filet de sécurité quand le calcul à vol d'oiseau est imprécis.",
      },
    ],
  },
  {
    id: 'ops',
    label: 'Alertes opérationnelles',
    fields: [
      {
        key: 'bus_offline_min',
        label: 'Bus hors ligne (min)',
        unit: 'min',
        min: 5,
        max: 120,
        help: 'Délai sans signal GPS avant l\'alerte "bus hors ligne". Bas = alerte plus rapide, mais plus de faux positifs sur les zones à signal GPS instable.',
      },
      {
        key: 'max_speed_kmh',
        label: 'Vitesse max (km/h)',
        unit: 'km/h',
        min: 30,
        max: 150,
        help: 'Vitesse au-delà de laquelle une alerte excès de vitesse est déclenchée (app chauffeur). Trop bas déclenchera des alertes sur route normale (autoroute, rocade).',
      },
      {
        key: 'trip_restart_cooldown_min',
        label: 'Cooldown redémarrage trajet (min)',
        unit: 'min',
        min: 5,
        max: 120,
        help: "Délai minimum après la fin d'un trajet avant qu'un nouveau puisse redémarrer automatiquement. Évite les redémarrages en boucle si le bus reste stationné près d'un arrêt.",
      },
    ],
  },
  {
    id: 'eta',
    label: 'Calcul ETA',
    fields: [
      {
        key: 'eta_speed_kmh',
        label: 'Vitesse ETA (km/h)',
        unit: 'km/h',
        min: 10,
        max: 100,
        help: "Vitesse moyenne utilisée pour estimer l'heure d'arrivée quand les données GPS/Mapbox en temps réel sont indisponibles.",
      },
      {
        key: 'eta_stop_time_s',
        label: 'Temps arrêt estimé (s)',
        unit: 's',
        min: 5,
        max: 300,
        help: "Temps ajouté par arrêt intermédiaire dans le calcul de l'ETA (montée/descente élèves).",
      },
    ],
  },
  {
    id: 'completion',
    label: 'Fin de trajet',
    fields: [
      {
        key: 'trip_completion_m',
        label: 'Seuil complétion trajet (m)',
        unit: 'm',
        min: 50,
        max: 1000,
        help: "Distance de l'école (ou du domicile chauffeur) à partir de laquelle le trajet est automatiquement marqué terminé.",
      },
    ],
  },
];

export const useThresholds = () => {
  const [thresholds, setThresholds] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await unifiedApi.thresholds.get();
      if (res.success) {
        setThresholds(res.data.thresholds);
        setDefaults(res.data.defaults);
        setDraft({ ...res.data.thresholds });
        setDirty(false);
      } else {
        toast.error('Impossible de charger les seuils');
      }
    } catch {
      toast.error('Erreur chargement des seuils');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await unifiedApi.thresholds.update(draft);
      if (res.success) {
        setThresholds(res.data);
        setDraft({ ...res.data });
        setDirty(false);
        toast.success('Seuils enregistrés');
      } else {
        toast.error(res.message || 'Erreur enregistrement');
      }
    } catch {
      toast.error('Erreur enregistrement des seuils');
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const handleReset = useCallback(() => {
    if (!defaults) return;
    setDraft({ ...defaults });
    setDirty(true);
  }, [defaults]);

  return {
    thresholds,
    defaults,
    draft,
    loading,
    saving,
    dirty,
    handleChange,
    handleSave,
    handleReset,
    GROUPS,
  };
};
